import { getFunctions, httpsCallable } from "firebase/functions";
import { normalizeSerialKey, splitSerialParts } from "../components/editModalFunctions";

export type VendorLookupResponse =
  | {
    ok: true;
    normalizedSerial: string;
    deepLink: string | null;
    model: string | null;
    warrantyStartDate: string | null;
    notes: string;
  }
  | {
    ok: false;
    normalizedSerial: string;
    deepLink: null;
    model: null;
    warrantyStartDate: null;
    notes: string;
  };

export type SuggestItemDetailsRequest = {
  serial: string;
  manufacturer?: string;
  productTypes: string[];
};
export type SuggestItemDetailsResponse = {
  ok: boolean;
  suggestedManufacturer?: string | null;
  suggestedModel?: string | null;
  suggestedProductType?: string | null;
};

const functions = getFunctions(undefined, "europe-west1");

export const vendorLookupCallable = httpsCallable<
  { manufacturer?: string; serial?: string },
  VendorLookupResponse
>(functions, "vendorLookup");

export const suggestItemDetailsCallable = httpsCallable<
  SuggestItemDetailsRequest,
  SuggestItemDetailsResponse
>(functions, "suggestItemDetails");

export async function callVendorLookup(manufacturer: string, serialRaw: string): Promise<VendorLookupResponse> {
  const base = splitSerialParts(serialRaw || "").base;
  const serial = normalizeSerialKey(base);
  const manu = (manufacturer || "").trim();

  try {
    const res = await vendorLookupCallable({ manufacturer: manu, serial });
    return (res.data as VendorLookupResponse) ?? {
      ok: false,
      normalizedSerial: serial,
      deepLink: null,
      model: null,
      warrantyStartDate: null,
      notes: "Tomt svar från vendorLookup",
    };
  } catch (err: any) {
    const msg =
      err?.message ||
      err?.details ||
      (typeof err === "string" ? err : "Anrop misslyckades");
    return {
      ok: false,
      normalizedSerial: serial,
      deepLink: null,
      model: null,
      warrantyStartDate: null,
      notes: msg,
    };
  }
}

export function registerVendorDevHelpers() {
  if (typeof window === "undefined") return;
  (window as any).testVendorLookup = async (m: string, s: string) => {
    const r = await callVendorLookup(m, s);
    console.log("vendorLookup —", r);
    return r;
  };

  (window as any).testVendorScrapeHp = async (serial: string) => {
    const fns = getFunctions(undefined, "europe-west1");
    const call = httpsCallable(fns, "vendorScrapeHp");
    const res = await call({ manufacturer: "hp", serial });
    console.log("scrapeHP —", res.data);
    return res.data;
  };
}

export function buildWarrantyLink(manufacturer: string, serialRaw: string): string | null {
  const manu = (manufacturer || "").trim().toLowerCase();
  const base = splitSerialParts(serialRaw || "").base;
  const serial = normalizeSerialKey(base);
  if (!serial) return null;
  if (["hp", "hewlett-packard", "hewlett packard"].includes(manu)) {
    return `https://support.hp.com/se-sv/check-warranty?serialnumber=${encodeURIComponent(serial)}`;
  }
  if (manu.includes("microsoft") || manu.includes("surface")) { return "https://mybusinessservice.surface.com/"; }
  if (manu.includes("lenovo")) { return "https://pcsupport.lenovo.com/se/sv/warrantylookup"; }
  return null;
}

export async function copyToClipboard(text: string) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    try { document.execCommand("copy"); } catch { /* Ignorera fallback-fel */ }
    document.body.removeChild(ta);
  }
}
